import { ReactNode, useContext, useEffect, useState } from "react";
import { Api } from "../../generated/Api";
import { AvisUtils, ErrorUtils, IAxiosError, apiConfig } from "../../utils";
import { errorDataLoading } from "../shared/appStates/appStates";
import { AvisLoader } from "../shared/components/index";
import { AvisContext } from "../shared/context/AvisContext";
import { ErrorPage } from "../shared/error/ErrorPage";
import { ForbiddenPage } from "../shared/error/ForbiddenPage";
import { Login } from "./Login";

interface IAuthComponentProps {
  children?: ReactNode;
}

export function AuthComponent(props: Readonly<IAuthComponentProps>) {
  const { children } = props;
  const {
    portalKind,
    ctxUser,
    isAuthorized,
    isLoading,
    login,
    logout,
    res,
    setupErrorInterceptors,
  } = useContext(AvisContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [isFirst, setIsFirst] = useState<boolean>(true);
  const [error, setError] = useState<IAxiosError>();
  const api = new Api(apiConfig);

  useEffect(() => {
    if (!isFirst || !ctxUser) {
      setError(undefined);
      setLoading(false);

      return;
    }

    setupErrorInterceptors();

    setLoading(true);
    setError(undefined);
    api
      .securityClaims()
      .then((res) => {
        setIsFirst(false);
        AvisUtils.updateLastActivityTime();
        login(res.data);
      })
      .catch((e) => {
        setError({ errorMessage: "", status: e.response?.status });
        if (e.response?.status === 401) logout(); // clear storage data
      })
      .finally(() => setLoading(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized]);

  if (loading || isLoading) return <AvisLoader />;

  if (
    ctxUser &&
    portalKind.requirePerimissions &&
    !ctxUser?.[portalKind.requirePerimissions]
  )
    return <ForbiddenPage showLogoutButton />;

  if (error || errorDataLoading.value) {
    // Unauthorized
    if (error?.status === 401 || errorDataLoading.value?.status === 401) {
      return <Login />;
    }

    return (
      <ErrorPage
        showToHomePageButton
        info={
          ErrorUtils.getGeneralErrorMessage(errorDataLoading.value, res) ??
          error?.errorMessage
        }
      />
    );
  }

  return isAuthorized ? children : <Login />;
}
